import { Element } from "@xmpp/xml"
import { DataFormsParser } from "@/lib/formParser"
import type {
  GpsServiceConfig,
  IdentityConfig,
  RoutingProvider,
} from "@/reducers/serviceConfigSlice"

export const SERVICE_CONFIG_NODE = "urn:slickshift:account-config:0"

const KNOWN_ROUTING_PROVIDERS: ReadonlySet<string> = new Set([
  "here",
  "ptv",
  "tomtom",
])

function parseRoutingProvider(value: string | undefined): RoutingProvider {
  if (value && KNOWN_ROUTING_PROVIDERS.has(value)) {
    return value as RoutingProvider
  }
  return "here"
}

/**
 * Parse the "identity" item from the `urn:slickshift:account-config:0` PubSub node.
 *
 * The payload is an XEP-0004 form containing:
 *   - `show-phone-with-name`: "1" | "0"  (vhost-wide toggle)
 *   - `whatsapp-number`: text, optional (WhatsApp number currently in use for this vhost)
 *
 * Returns `undefined` if the item cannot be parsed.
 */
export function parseIdentityConfigItem(
  item: Element,
): IdentityConfig | undefined {
  const form = DataFormsParser.parse(item)
  if (!form) return undefined
  const raw = form.fields?.["show-phone-with-name"]?.values?.[0]
  const whatsappNumber =
    form.fields?.["whatsapp-number"]?.values?.[0]?.trim() || undefined
  if (raw === undefined && whatsappNumber === undefined) return undefined
  return {
    showPhoneWithName: raw === "1" || raw === "true",
    ...(whatsappNumber ? { whatsappNumber } : {}),
  }
}

/**
 * Parse the "gps" item from the `urn:slickshift:account-config:0` PubSub node.
 *
 * The payload is an XEP-0004 form containing:
 *   - `routing-provider`: "here" | "ptv" | "tomtom"
 *
 * Returns `undefined` if the item cannot be parsed.
 */
export function parseGpsServiceConfigItem(
  item: Element,
): GpsServiceConfig | undefined {
  const form = DataFormsParser.parse(item)
  if (!form) return undefined

  const routingProvider = parseRoutingProvider(
    form.fields?.["routing-provider"]?.values?.[0],
  )

  return { routingProvider }
}

/**
 * Parse a live PubSub event stanza for the service-config node.
 *
 * Returns `{ itemId, config }` on publish, `{ itemId, retract: true }` on
 * retract, or `undefined` if the stanza does not match the node.
 */
export type ServiceConfigEvent =
  | { itemId: "gps"; config: GpsServiceConfig }
  | { itemId: "identity"; config: IdentityConfig }
  | { itemId: string; retract: true }

export function parseServiceConfigEvent(
  stanza: Element,
): ServiceConfigEvent | undefined {
  const eventNode = stanza.getChild(
    "event",
    "http://jabber.org/protocol/pubsub#event",
  )
  if (!eventNode) return undefined

  const itemsNode = eventNode.getChildByAttr("node", SERVICE_CONFIG_NODE)
  if (!itemsNode) return undefined

  // Retract
  const retractNode = itemsNode.getChild("retract")
  if (retractNode) {
    const itemId = retractNode.getAttr("id")
    if (!itemId) return undefined
    return { itemId, retract: true }
  }

  // Publish
  const itemNode = itemsNode.getChild("item")
  if (!itemNode) return undefined

  const itemId = itemNode.getAttr("id")
  if (!itemId) return undefined

  if (itemId === "gps") {
    const config = parseGpsServiceConfigItem(itemNode)
    if (!config) return undefined
    return { itemId: "gps", config }
  }

  if (itemId === "identity") {
    const config = parseIdentityConfigItem(itemNode)
    if (!config) return undefined
    return { itemId: "identity", config }
  }

  return undefined
}
